import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import MarkdownEditor from "../MarkdownEditor";
import ConfirmModal from "../modals/ConfirmModal";
import RefineModal from "../modals/RefineModal";
import PreviewModal from "../modals/PreviewModal";
import HistoryView from "../HistoryView";
import { documentsService } from "../../services/documents";
import type { DocumentType, DocumentVersion, DocumentHistoryItem } from "../../types";

export interface DocumentTabHandle {
  generate: () => void;
  refineClick: () => void;
  save: () => void;
  showHistory: () => void;
  backToEditor: () => void;
  preview: () => void;
  readonly dirty: boolean;
  readonly generating: boolean;
}

interface Props {
  jobId: string;
  docType: DocumentType;
  label: string;
  onPreviewRequest?: (versionId: string | null) => void;
  onDirtyChange?: (dirty: boolean) => void;
  onGeneratingChange?: (generating: boolean) => void;
  onViewChange?: (view: "editor" | "history") => void;
}

const DocumentTab = forwardRef<DocumentTabHandle, Props>(function DocumentTab(
  { jobId, docType, label, onPreviewRequest, onDirtyChange, onGeneratingChange, onViewChange },
  ref
) {
  const [view, setView] = useState<"editor" | "history">("editor");
  const [content, setContent] = useState("");
  const [latestVersion, setLatestVersion] = useState<DocumentVersion | null>(null);
  const [generating, setGenerating] = useState(false);
  const [confirmGenerateOpen, setConfirmGenerateOpen] = useState(false);
  const [refineOpen, setRefineOpen] = useState(false);
  const [refineInitialText, setRefineInitialText] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewVersionId, setPreviewVersionId] = useState<string | null>(null);
  const [previewContent, setPreviewContent] = useState("");

  const dirty =
    latestVersion !== null ? content !== latestVersion.content : content.length > 0;

  const previewContext =
    previewVersionId !== null ? "history" : dirty ? "unsaved" : "saved";

  useEffect(() => { onDirtyChange?.(dirty); }, [dirty]);
  useEffect(() => { onGeneratingChange?.(generating); }, [generating]);
  useEffect(() => { onViewChange?.(view); }, [view]);

  useEffect(() => {
    loadLatest();
  }, [jobId, docType]);

  async function loadLatest() {
    try {
      const version = await documentsService.getLatest(jobId, docType);
      setLatestVersion(version);
      setContent(version.content);
    } catch {
      setLatestVersion(null);
      setContent("");
    }
  }

  function generate() {
    if (dirty) {
      setConfirmGenerateOpen(true);
      return;
    }
    void doGenerate();
  }

  async function doGenerate() {
    const pre = content;
    // preStreamContent captured in local `pre` for rollback
    setGenerating(true);
    setError(null);
    setContent("");
    try {
      let acc = "";
      for await (const chunk of documentsService.streamGenerate(jobId, docType)) {
        acc += chunk;
        setContent(acc);
      }
      const version = await documentsService.save(jobId, docType, acc, "__generated__");
      setLatestVersion(version);
    } catch (err) {
      setContent(pre);
      setError(err instanceof Error ? err.message : "Generation failed.");
    } finally {
      setGenerating(false);
    }
  }

  function refineClick() {
    setRefineInitialText("");
    setRefineOpen(true);
  }

  async function applyRefine(instruction: string) {
    setRefineOpen(false);
    const pre = content;
    // preStreamContent captured in local `pre` for rollback
    setGenerating(true);
    setError(null);
    setContent("");
    try {
      let acc = "";
      for await (const chunk of documentsService.streamRefine(jobId, docType, instruction, pre)) {
        acc += chunk;
        setContent(acc);
      }
      const version = await documentsService.save(jobId, docType, acc, instruction);
      setLatestVersion(version);
    } catch (err) {
      setContent(pre);
      setError(err instanceof Error ? err.message : "Refinement failed.");
    } finally {
      setGenerating(false);
    }
  }

  async function save() {
    try {
      const version = await documentsService.save(jobId, docType, content);
      setLatestVersion(version);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
    }
  }

  function showHistory() {
    setView("history");
  }

  function preview() {
    setPreviewContent(content);
    setPreviewVersionId(null);
    setPreviewOpen(true);
    onPreviewRequest?.(null);
  }

  async function handlePreviewSaveAndDownload() {
    const version = await documentsService.save(jobId, docType, content);
    setLatestVersion(version);
    await documentsService.downloadPdf(
      documentsService.exportVersionPdfUrl(jobId, docType, version.version_id)
    );
  }

  async function handlePreviewDownload() {
    if (previewVersionId) {
      await documentsService.downloadPdf(
        documentsService.exportVersionPdfUrl(jobId, docType, previewVersionId)
      );
    } else {
      await documentsService.downloadPdf(
        documentsService.exportPdfUrl(jobId, docType)
      );
    }
  }

  async function handlePreviewRestore() {
    if (!previewVersionId) return;
    try {
      const version = await documentsService.restore(jobId, docType, previewVersionId);
      setContent(version.content);
      setLatestVersion(version);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Restore failed.");
    }
    setPreviewOpen(false);
    setView("editor");
  }

  async function handleHistoryRestore(item: DocumentHistoryItem) {
    try {
      const version = await documentsService.restore(jobId, docType, item.version_id);
      setContent(version.content);
      setLatestVersion(version);
      setView("editor");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Restore failed.");
    }
  }

  function handleHistoryPreview(item: DocumentHistoryItem) {
    setPreviewContent(item.content ?? "");
    setPreviewVersionId(item.version_id);
    setPreviewOpen(true);
  }

  useImperativeHandle(ref, () => ({
    generate,
    refineClick,
    save,
    showHistory,
    backToEditor: () => setView("editor"),
    preview,
    get dirty() { return dirty; },
    get generating() { return generating; },
  }));

  const showPlaceholder = !generating && !content;

  return (
    <>
      <div className="h-full overflow-y-auto">
        {error && (
          <div
            role="alert"
            className="mb-4 px-3 py-2 rounded bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm"
          >
            {error}
          </div>
        )}

        {view === "history" ? (
          <HistoryView
            jobId={jobId}
            docType={docType}
            latestVersionId={latestVersion?.version_id ?? null}
            dirty={dirty}
            onRestore={handleHistoryRestore}
            onPreview={handleHistoryPreview}
          />
        ) : showPlaceholder ? (
          <p
            data-testid={`placeholder-${docType}`}
            className="text-slate-500 dark:text-slate-400 italic"
          >
            No document yet. Click &lsquo;Generate&rsquo; to create one with AI.
          </p>
        ) : (
          <MarkdownEditor
            value={content}
            onChange={(v) => { setContent(v); setError(null); }}
            label={label}
            placeholder="No document yet. Click 'Generate' to create one with AI."
            isGenerating={generating}
          />
        )}
      </div>

      <ConfirmModal
        open={confirmGenerateOpen}
        title="Discard unsaved changes?"
        message="Generating a new document will replace your current unsaved changes. This cannot be undone."
        confirmLabel="Generate anyway"
        onConfirm={() => { setConfirmGenerateOpen(false); void doGenerate(); }}
        onCancel={() => setConfirmGenerateOpen(false)}
      />

      <RefineModal
        open={refineOpen}
        initialText={refineInitialText}
        onApply={applyRefine}
        onCancel={() => setRefineOpen(false)}
      />

      <PreviewModal
        open={previewOpen}
        content={previewContent}
        context={previewContext}
        onClose={() => setPreviewOpen(false)}
        onDownload={handlePreviewDownload}
        onSaveAndDownload={handlePreviewSaveAndDownload}
        onRestore={handlePreviewRestore}
      />
    </>
  );
});

export default DocumentTab;
